import { subscribe, getState, normalizeIdentityKey, openModalForEdit } from '../state/store.js';
import { formatDateForDisplayValue, getDateSortValue } from '../utils/dates.js';
import { getHighestCharacterLevel } from '../utils/levels.js';

const memoriamGrid = document.getElementById('memoriamGrid');
const memoriamSort = document.getElementById('memoriamSort');
const SORT_ASC = '▲';
const SORT_DESC = '▼';
const DEFAULT_SORT_DIRECTION = { name: 'asc', level: 'asc', deathday: 'desc' };

let sortState = { key: 'deathday', direction: 'desc' };

const isDeadStatus = (status) => String(status || '').trim().toLowerCase() === 'dead';

const getSortValue = (character, key) => {
  switch (key) {
    case 'level':
      return getHighestCharacterLevel(character.level, 0);
    case 'name':
      return (character.name || '').toLowerCase();
    case 'deathday':
    default:
      return getDateSortValue(character.deathday);
  }
};

const sortDead = (dead) => {
  const { key, direction } = sortState;
  const dir = direction === 'asc' ? 1 : -1;
  return dead.sort((a, b) => {
    const va = getSortValue(a, key);
    const vb = getSortValue(b, key);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });
};

const updateSortIndicators = () => {
  memoriamSort?.querySelectorAll('.sort-btn').forEach((btn) => {
    const active = btn.dataset.sort === sortState.key;
    btn.classList.toggle('active', active);
    btn.setAttribute('data-indicator', active ? (sortState.direction === 'asc' ? SORT_ASC : SORT_DESC) : '');
  });
};

const canEditCharacter = (character, state) => {
  if (state.viewMode === 'dm') return true;
  if (state.viewMode === 'player' && state.identity && !state.identity.isGuest) {
    return normalizeIdentityKey(character.player) === normalizeIdentityKey(state.identity.name);
  }
  return false;
};

const escapeHTML = (value = '') =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const renderCard = (character, state) => {
  const classRaceLine = [character.race, character.class].filter(Boolean).join(' ');
  const fellAt = character.level ? ` — fell at Level ${escapeHTML(character.level)}` : '';
  const cause = character.cause_of_death
    ? `<p class="memoriam-cause">"${escapeHTML(character.cause_of_death)}"</p>`
    : '';
  const diedDate = character.deathday ? formatDateForDisplayValue(character.deathday) : 'Unknown date';
  const player = character.player || 'Unknown Player';
  const plateClass = character.portrait_url ? 'plate dead has-image' : 'plate dead';
  const plateStyleAttr = character.portrait_url
    ? ` style="background-image:url('${escapeHTML(character.portrait_url)}')"`
    : '';
  const editButton = canEditCharacter(character, state)
    ? `<div class="memoriam-actions"><button class="btn small outline" type="button" data-action="edit">Edit</button></div>`
    : '';

  return `<article class="memoriam-card" data-id="${character.id}">
    <div class="${plateClass}"${plateStyleAttr}><span class="plate-hint">Portrait</span></div>
    <div class="memoriam-main">
      <h3>${escapeHTML(character.name || 'Unnamed')}</h3>
      <p class="memoriam-class-line">${escapeHTML(classRaceLine)}${fellAt}</p>
      ${cause}
      <p class="memoriam-meta">Died ${escapeHTML(diedDate)} &middot; played by ${escapeHTML(player)}</p>
      ${editButton}
    </div>
  </article>`;
};

const renderMemoriam = () => {
  if (!memoriamGrid) return;
  const state = getState();
  const { characters } = state;
  const dead = sortDead(characters.filter((c) => isDeadStatus(c.status)));
  memoriamGrid.innerHTML = dead.length
    ? dead.map((c) => renderCard(c, state)).join('')
    : '<p class="placeholder">No losses yet — long may it last.</p>';
  updateSortIndicators();
};

export const initMemoriam = () => {
  subscribe(renderMemoriam);
  renderMemoriam();

  memoriamGrid?.addEventListener('click', (event) => {
    if (event.target?.dataset?.action !== 'edit') return;
    const card = event.target.closest('[data-id]');
    if (!card) return;
    openModalForEdit(Number(card.dataset.id));
  });

  memoriamSort?.addEventListener('click', (event) => {
    const key = event.target.closest('.sort-btn')?.dataset?.sort;
    if (!key) return;
    sortState =
      sortState.key === key
        ? { key, direction: sortState.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: DEFAULT_SORT_DIRECTION[key] || 'asc' };
    renderMemoriam();
  });
};
